/**
 * The referral programme's shared value sets.
 *
 * A **referrer** introduces a customer, the customer types that referrer's
 * **referral code** at checkout, and the referrer plus their **referral
 * partner** company earn on what they brought in. Everything here is declared
 * once: `packages/db` builds the pgEnums FROM these arrays, and the API's Zod
 * schemas read the same values, so a value added here reaches both layers.
 *
 * The lexicon is generic on purpose. The first channel this was built for is
 * one KIND of referrer, and it appears here only as a `ReferrerType` value and
 * its display label — never as the name of the concept.
 * `scripts/referral-lexicon.test.ts` holds that at zero.
 */

/**
 * What job a referrer does. The partner is the COMPANY; the people who work
 * there, and hold the codes, are what carry a type.
 *
 * **A label, and nothing more.** It filters and groups the ops list; it must
 * never reach rate resolution — rates are negotiated per partner.
 */
export const REFERRER_TYPES = ['property_manager', 'leasing_agent'] as const

export type ReferrerType = (typeof REFERRER_TYPES)[number]

/** Display labels for the ops desk. One registry, read by every surface. */
export const REFERRER_TYPE_LABELS: Record<ReferrerType, string> = {
  property_manager: 'Property manager',
  leasing_agent: 'Leasing agent',
}

/**
 * Who a compensation rule pays.
 *
 * The referrer is the person who made the introduction; the partner is the
 * company they made it under. A partner earning both an upfront fee and a
 * monthly trail is two rows, not one exotic kind.
 */
export const REFERRAL_RATE_PAYEES = ['referrer', 'partner'] as const

export type ReferralRatePayee = (typeof REFERRAL_RATE_PAYEES)[number]

/**
 * What a compensation rule multiplies.
 *
 * `per_pallet` and `per_order` read `value_cents`; `percent_of_order` reads
 * `value_bps`. Which column a basis interprets is stated once, in
 * `referralRateValueColumn` below, so no consumer has to re-derive it.
 */
export const REFERRAL_RATE_BASES = [
  'per_order',
  'per_pallet',
  'percent_of_order',
] as const

export type ReferralRateBasis = (typeof REFERRAL_RATE_BASES)[number]

/**
 * How often a compensation rule pays.
 *
 * The two cadences accrue from different triggers and are therefore built in
 * different phases: `once` fires inside the charge transaction, `recurring`
 * when a storage cycle is collected in cash.
 */
export const REFERRAL_RATE_CADENCES = ['once', 'recurring'] as const

export type ReferralRateCadence = (typeof REFERRAL_RATE_CADENCES)[number]

/**
 * The cadences something actually ACCRUES today.
 *
 * Not a list of what the editor offers — it still offers the rest, and says so.
 *
 * `recurring` is declared, stored and understood everywhere: the pgEnum, the
 * ledger's snapshot columns, the rate's own term rules and this file's labels.
 * What does not exist is the half that PAYS it. Storage Trail is built and
 * parked (ruled: the programme is one-time only for now), so a
 * recurring rate is a deal a partner would be promised and the system would
 * never honour — and nothing anywhere would say so.
 *
 * **This is an OFFER list, not enforcement, and the difference is the point.**
 * `referralRatesService` still accepts and validates a recurring rate, so the
 * API and any future surface can write one; the term rules stay live and
 * covered rather than being deleted and re-added when the accrual lands.
 * Closing the API too would mean gutting eleven tests of correct logic that
 * returns with that phase. What this removes is the door an operator can walk
 * through by accident, and `formatRateSummary` says the rest out loud for any
 * recurring row that exists however it was made.
 *
 * Spelled as an explicit list rather than as the complement of a banned one: a
 * cadence added to the taxonomy is then absent here and is described as paying
 * nothing, which is the safe direction.
 *
 * **It is the notice policy, not the accrual.** The half that actually pays a
 * cadence lives in `referral-commission.service.ts`; reviving Storage Trail
 * means landing that branch AND adding `recurring` here, and this list going
 * stale in either direction is a surface telling an operator the wrong thing.
 */
export const CADENCES_THAT_ACCRUE = [
  'once',
] as const satisfies readonly ReferralRateCadence[]

/**
 * Whether anything accrues a rate on this cadence today.
 *
 * The one predicate all three notices read — the editor's, the save
 * confirmation's and the stored row's — so they cannot come to disagree about
 * one rate inside a single save.
 *
 * `.some` rather than `.includes` on a widened array: `as readonly string[]`
 * would be a cast to silence the checker, which `10-coding-style.md` forbids.
 */
export function cadenceAccruesToday(cadence: ReferralRateCadence): boolean {
  return CADENCES_THAT_ACCRUE.some((accruing) => accruing === cadence)
}

/**
 * Which value column a basis interprets.
 *
 * An exhaustive switch rather than a lookup built from a hand-listed subset:
 * a basis added to the taxonomy fails the build here instead of silently
 * landing in whichever branch is the complement.
 */
export function referralRateValueColumn(
  basis: ReferralRateBasis,
): 'value_cents' | 'value_bps' {
  switch (basis) {
    case 'per_order':
    case 'per_pallet':
      return 'value_cents'
    case 'percent_of_order':
      return 'value_bps'
    default: {
      const exhaustive: never = basis
      throw new Error(`Unhandled referral rate basis: ${String(exhaustive)}`)
    }
  }
}

/**
 * Absolute ceilings on a rate's stored value — the fat-finger guard, not a
 * business rule.
 *
 * A typo pays real money, faithfully and repeatedly: `2500` typed as `25000`
 * accrues $250 per pallet, monthly, to someone outside the company, and
 * nothing downstream can catch it because accrual is correct to pay whatever
 * the rate says.
 *
 * These are the HARD limits — the widest a value may ever be, enforced by the
 * router's Zod schema and again by the service. The working limit is lower and
 * lives in `app_config` (`REFERRAL_RATE_LIMIT_KEYS`) so it can be raised
 * without a deploy; the service clamps any configured value to these.
 */
export const REFERRAL_RATE_HARD_LIMITS = {
  /** $10,000 per order or per pallet. */
  maxValueCents: 1_000_000,
  /** 100% — basis points cannot exceed the whole. */
  maxValueBps: 10_000,
  /** 50 years, in months. A term beyond this is indistinguishable from none. */
  maxRecurringMonths: 600,
} as const

/**
 * The working limits' `app_config` keys, with the defaults applied when no row
 * exists.
 *
 * Non-secret rows, and there is no row for either until somebody creates one —
 * the fallback below is what applies on a fresh database. They are written
 * through the app-config surface, which is superadmin-only
 * (`appConfig.*` on `adminProcedure`), NOT by the BD operator who hits the
 * ceiling; raising one is an admin act, deliberately.
 *
 * The cents default is deliberately near the programme's own numbers rather
 * than near the hard ceiling — $100 per unit catches the 10× typo of a $25
 * rate, which a ceiling of $10,000 does not.
 */
export const REFERRAL_RATE_LIMIT_KEYS = {
  maxValueCents: {
    key: 'referrals.max_rate_value_cents',
    fallback: 10_000,
  },
  maxRecurringMonths: {
    key: 'referrals.max_rate_recurring_months',
    fallback: 60,
  },
} as const

/**
 * How long a minted referral code is. Eight characters of the 32-glyph
 * unambiguous alphabet is ~40 bits — enough that guessing one is not a
 * profitable way to get a stranger's discount.
 */
export const REFERRAL_CODE_LENGTH = 8

/**
 * Normalize a code the way the database stores it.
 *
 * The `code` column always holds this form, whatever was typed — a readable
 * code keeps its typed casing in `display_code` — and carries a unique index
 * on it, so case-insensitive matching is free: every read normalizes its input
 * rather than lowercasing the column.
 */
export function normalizeReferralCode(code: string): string {
  return code.trim().toUpperCase()
}

/**
 * What a customer gets off their first order for entering a valid code.
 *
 * Flat, whatever the pallet count — a first order under this amount is not
 * reachable, and `computeDiscountCents` clamps it to Stripe's minimum anyway.
 *
 * **It is the referral's OFFER, not what the order is charged.** The customer
 * receives the larger of this and any automatic promo, never both, and the
 * redemption is recorded whichever applied — so nothing downstream may read
 * this constant, or an order's discount line, as the other.
 */
export const REFERRAL_CODE_DISCOUNT_CENTS = 2500

/** The offer as a customer reads it — "$25" — from the constant, never typed. */
export function referralOfferDollars(): string {
  const dollars = REFERRAL_CODE_DISCOUNT_CENTS / 100
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

/**
 * A readable code: the referrer's first name, their last initial, the building
 * and the offer — `NeishaDWestfield25`. Ruled by Marcus, accepting
 * that such a code is guessable by anyone who knows the person and the
 * building; referrers hand theirs out openly anyway. A random code is still
 * what a code issued with no building gets.
 *
 * The suffix is the offer in dollars at the moment of issue. A code is a
 * permanent string, so a later change to the offer does not rename old codes.
 */
export const READABLE_CODE_SUFFIX = String(REFERRAL_CODE_DISCOUNT_CENTS / 100)

/**
 * The path a referral QR opens: `/r/<code>`. One declaration, read by the QR,
 * the route that catches it and the checkout scanner that decodes it, so the
 * three cannot come to disagree.
 */
export const REFERRAL_LINK_PREFIX = '/r/' as const

/** The route path that catches the link — derived, so the two cannot differ. */
export const REFERRAL_LINK_ROUTE = `${REFERRAL_LINK_PREFIX}$code` as const

export function referralLinkPath(code: string): string {
  return `${REFERRAL_LINK_PREFIX}${encodeURIComponent(code)}`
}

/**
 * The code in whatever a scan produced: a referral link (from any origin — a
 * printed QR outlives a domain move) or a bare code. Null for a link that names
 * no code; the caller decides what an unreadable scan becomes.
 */
export function referralCodeFromScan(raw: string): string | null {
  const value = raw.trim()
  const isLink = /^https?:\/\//i.test(value) || /^\/r\//i.test(value)
  if (!isLink) return value || null
  // Case-insensitive: a QR generator may uppercase a whole URL to use its
  // denser alphanumeric mode, and the code itself survives that anyway.
  // Searched in the ORIGINAL string — lowercasing can change its length.
  const at = value.search(/\/r\//i)
  if (at < 0) return null
  const tail =
    value.slice(at + REFERRAL_LINK_PREFIX.length).split(/[/?#]/)[0] ?? ''
  try {
    return decodeURIComponent(tail).trim() || null
  } catch {
    return null
  }
}

/** What an operator may type as a code: letters and digits, nothing else. */
export const READABLE_CODE_MAX_LENGTH = 40
export const READABLE_CODE_PATTERN = new RegExp(
  `^[A-Za-z0-9]{4,${READABLE_CODE_MAX_LENGTH}}$`,
)

/**
 * A building's code name — the word its readable codes are suggested from —
 * holds this many CHARACTERS (code points), not UTF-16 units: the data
 * migration that names buildings cuts it with SQL `left(…, 40)`, which counts
 * code points, so the form and the API count the same way or a migrated name
 * holding an emoji could not be saved again. Free text, unlike a code.
 */
export const CODE_NAME_MAX_LENGTH = 40

/** Whether a code name fits, counted as the migration counts it. */
export function codeNameFits(codeName: string): boolean {
  return [...codeName].length <= CODE_NAME_MAX_LENGTH
}

/** A code name cut to fit, by code point — never mid surrogate pair. */
export function clipCodeName(codeName: string): string {
  return [...codeName].slice(0, CODE_NAME_MAX_LENGTH).join('')
}

/** Letters and digits only, with accents folded to their base letter. */
function alphanumeric(text: string): string {
  return text.normalize('NFD').replace(/[^A-Za-z0-9]/g, '')
}

function capitalized(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/**
 * The code a referrer at a building is offered, or null when either half is
 * missing. Every word of the building keeps its own capital so the result
 * reads (`Roselle Park 10` → `RosellePark10`); the operator can still edit it.
 */
export function suggestReferralCode(
  name: string,
  building: string,
): string | null {
  const words = name.trim().split(/\s+/).map(alphanumeric).filter(Boolean)
  const first = words[0]
  const buildingPart = building
    .trim()
    .split(/\s+/)
    .map((w) => capitalized(alphanumeric(w)))
    .join('')
  if (!first || !buildingPart) return null
  const lastInitial =
    words.length > 1 ? (words.at(-1)?.charAt(0).toUpperCase() ?? '') : ''
  // Always inside the pattern's 40: a long name or building is cut, never the
  // suffix, so a suggestion is one the service will take.
  const head = `${capitalized(first).slice(0, 16)}${lastInitial}`
  const room =
    READABLE_CODE_MAX_LENGTH - head.length - READABLE_CODE_SUFFIX.length
  return `${head}${buildingPart.slice(0, room)}${READABLE_CODE_SUFFIX}`
}

/**
 * Where a redemption stands.
 *
 * `reserved` is written BEFORE Stripe is asked to charge anything, because the
 * charge happens before the order transaction opens: a unique-index refusal
 * that arrived only inside that transaction would leave a customer charged the
 * reduced amount with nothing to undo. `consumed` is written inside the order
 * transaction, and names the order.
 *
 * There is deliberately no `released`/`expired` value. A reservation whose
 * checkout provably will never pay is DELETED, which is what frees the owner's
 * one-per-customer slot — a terminal status would have to be excluded from both
 * partial unique indexes, and a predicate forgotten there locks a customer out
 * of a discount they never received.
 */
export const REFERRAL_REDEMPTION_STATUSES = ['reserved', 'consumed'] as const

export type ReferralRedemptionStatus =
  (typeof REFERRAL_REDEMPTION_STATUSES)[number]

/**
 * Which discount an order's charge actually used — the automatic promo or the
 * referral code's offer. Surfaced so the checkout can tell a customer their
 * code was recorded even when a bigger promo is the one taking money off.
 */
export const APPLIED_DISCOUNT_SOURCES = ['promo', 'referral'] as const

export type AppliedDiscountSource = (typeof APPLIED_DISCOUNT_SOURCES)[number]

/**
 * Why a referral commission entry exists.
 *
 * The ledger is append-only: a balance is a `SUM`, and nothing is ever edited
 * back. An `accrual` is what a rule earned when the order was charged; a
 * `reversal` is the offsetting negative row written when one of that order's
 * trips is cancelled with its money returned. A `challenge_reward` is what a
 * referrer won by meeting a challenge Ops set up. There is deliberately no
 * status to flip — a status can say an entry IS reversed, never that it WAS.
 */
export const REFERRAL_COMMISSION_REASONS = [
  'accrual',
  'reversal',
  'challenge_reward',
] as const

export type ReferralCommissionReason =
  (typeof REFERRAL_COMMISSION_REASONS)[number]

/**
 * Whether an entry of this reason is money EARNED — counted into what a payee
 * has earned and what their payouts cover — rather than money taken back.
 *
 * Exhaustive, so a reason added later fails the build here instead of landing
 * in whichever side a hand-written `reason = 'accrual'` happened to put it. A
 * challenge reward is an earning that nothing reverses: only an `accrual` is
 * offset when a trip is cancelled (ruled, a won reward stays won).
 */
export function isEarningReason(reason: ReferralCommissionReason): boolean {
  switch (reason) {
    case 'accrual':
    case 'challenge_reward':
      return true
    case 'reversal':
      return false
    default: {
      const exhaustive: never = reason
      throw new Error(`Unhandled commission reason: ${String(exhaustive)}`)
    }
  }
}

/**
 * Whether an entry is COMMISSION — produced by a partner's rule, as against a
 * reward from Stopal's own programme. The partner rollup shows commission only.
 * Exhaustive for the same reason as `isEarningReason`.
 */
export function isCommissionReason(reason: ReferralCommissionReason): boolean {
  switch (reason) {
    case 'accrual':
    case 'reversal':
      return true
    case 'challenge_reward':
      return false
    default: {
      const exhaustive: never = reason
      throw new Error(`Unhandled commission reason: ${String(exhaustive)}`)
    }
  }
}

/** The commission reasons, derived for SQL readers. */
export const COMMISSION_REASONS: readonly ReferralCommissionReason[] =
  REFERRAL_COMMISSION_REASONS.filter(isCommissionReason)

/** The earning reasons, derived from the predicate above for SQL readers. */
export const EARNING_COMMISSION_REASONS: readonly ReferralCommissionReason[] =
  REFERRAL_COMMISSION_REASONS.filter(isEarningReason)
